// Shared "example plan" calculation model used by the homepage example card
// and the full /plan page. Deliberately simple, transparent arithmetic —
// illustrative, not a real solar-yield model, but internally consistent so
// every number on screen reacts correctly when you change an input.

import { AI_PICK_BATTERY_KWH } from "./batteryOptions";

/** 0 means no battery. Other values are usable kWh. */
export type BatteryOption = number;

export interface PlanInputs {
  panels: number;
  battery: BatteryOption;
  ev: boolean;
  heatPump: boolean;
  monthlyBill: number; // current spend, £/month
}

export const DEFAULT_INPUTS: PlanInputs = {
  panels: 12,
  battery: AI_PICK_BATTERY_KWH,
  ev: false,
  heatPump: false,
  monthlyBill: 180,
};

const PANEL_WATTS = 480; // W per panel
const PRICE_PER_PANEL = 480; // £ installed, per panel, blended with inverter/labour
const BATTERY_PRICE: Record<string, number> = {
  "0": 0,
  "5.76": 3500,
  "11.52": 6100,
  "17.28": 8200,
  "23.04": 10200,
  "28.8": 12200,
  "34.56": 14200,
  "40.32": 16200,
};

function batteryPrice(kwh: number): number {
  return BATTERY_PRICE[String(kwh)] ?? 0;
}
const BASE_INSTALL_FEE = 1400; // scaffolding, inverter, wiring, commissioning

/** Years of year-1 benefit shown as the homepage home-value band when a catalog package is selected. */
export const CATALOG_UPLIFT_YEARS = { low: 5, high: 10 } as const;

/** Installed price and year-1 benefit taken from an active system option. */
export interface PricedExamplePackage {
  cost: number;
  annualBenefit: number;
  systemOutputW: number | null;
}

export interface PlanOutputs {
  kwp: number;
  systemPrice: number;
  annualGenerationKwh: number;
  annualBenefit: number;
  monthlyBenefit: number;
  paybackYears: number;
  net25: number;
  roiPct: number;
  independencePct: number;
  epcBefore: string;
  epcAfter: string;
  homeValueUpliftLow: number;
  homeValueUpliftHigh: number;
  seriesYears: number[];
  cumulative: number[]; // cumulative net position (savings - cost) per year, 0..25
}

const EPC_LETTERS = ["G", "F", "E", "D", "C", "B", "A"];

function roundTo100(value: number): number {
  return Math.round(value / 100) * 100;
}

export function computePlan(inputs: PlanInputs, priced?: PricedExamplePackage | null): PlanOutputs {
  const { panels, battery, ev, heatPump, monthlyBill } = inputs;
  const outputW = priced?.systemOutputW;
  const kwp =
    outputW != null && outputW > 0
      ? Math.round((outputW / 1000) * 100) / 100
      : Math.round(((panels * PANEL_WATTS) / 1000) * 100) / 100;

  const systemPrice = priced
    ? priced.cost
    : BASE_INSTALL_FEE + panels * PRICE_PER_PANEL + batteryPrice(battery);

  // ~950 kWh/kWp/year is a reasonable illustrative UK yield.
  const annualGenerationKwh = Math.round(kwp * 950);

  const currentAnnualSpend = monthlyBill * 12;
  const hasBattery = battery > 0;

  // Self-consumption + export income + arbitrage, each modelled simply.
  const selfConsumeShare = hasBattery ? 0.65 : 0.4;
  const importRate = 0.28; // £/kWh illustrative
  const exportRate = 0.15; // £/kWh illustrative
  const usedOnSite = annualGenerationKwh * selfConsumeShare;
  const exported = annualGenerationKwh - usedOnSite;
  const billOffset = usedOnSite * importRate;
  const exportIncome = exported * exportRate;
  const arbitrage = hasBattery ? Math.round(battery * 14) : 0;
  const evBoost = ev ? 180 : 0;
  const heatPumpDrag = heatPump ? -90 : 0; // more demand, slightly longer combined payback

  const modelledBenefit = Math.max(
    0,
    Math.round(billOffset + exportIncome + arbitrage + evBoost + heatPumpDrag)
  );
  // Catalog packages already price the battery into `cost` and the year-1
  // benefit into `savings`. EV and heat-pump toggles still nudge that benefit.
  const annualBenefit = priced
    ? Math.max(0, Math.round(priced.annualBenefit + evBoost + heatPumpDrag))
    : modelledBenefit;
  const monthlyBenefit = Math.round(annualBenefit / 12);

  const paybackYears = annualBenefit > 0 ? Math.round((systemPrice / annualBenefit) * 10) / 10 : 99;

  // 25-year forecast: electricity prices assumed to rise ~4%/yr; benefit grows with them.
  const years = Array.from({ length: 26 }, (_, i) => i);
  const cumulative: number[] = [];
  let running = -systemPrice;
  for (const y of years) {
    if (y === 0) {
      cumulative.push(running);
      continue;
    }
    const yearBenefit = annualBenefit * Math.pow(1.04, y - 1);
    running += yearBenefit;
    cumulative.push(Math.round(running));
  }
  const net25 = cumulative[25];
  const roiPct = systemPrice > 0 ? Math.round((annualBenefit / systemPrice) * 1000) / 10 : 0;

  const independencePct = Math.min(
    99,
    Math.round((usedOnSite / Math.max(currentAnnualSpend / importRate, 1)) * 100)
  );

  const epcIndex = 3; // baseline D
  const epcBoost = Math.min(3, Math.round(kwp / 2) + (hasBattery ? 1 : 0));
  const epcBefore = EPC_LETTERS[epcIndex];
  const epcAfter = EPC_LETTERS[Math.min(EPC_LETTERS.length - 1, epcIndex + epcBoost)];

  const homeValueBase = 425000;
  const homeValueUpliftLow = priced
    ? roundTo100(annualBenefit * CATALOG_UPLIFT_YEARS.low)
    : Math.round((homeValueBase * 0.009) / 100) * 100;
  const homeValueUpliftHigh = priced
    ? roundTo100(annualBenefit * CATALOG_UPLIFT_YEARS.high)
    : Math.round((homeValueBase * 0.02) / 100) * 100;

  return {
    kwp,
    systemPrice,
    annualGenerationKwh,
    annualBenefit,
    monthlyBenefit,
    paybackYears,
    net25,
    roiPct,
    independencePct,
    epcBefore,
    epcAfter,
    homeValueUpliftLow,
    homeValueUpliftHigh,
    seriesYears: years,
    cumulative,
  };
}
